import React, { useState, useEffect, useCallback, useRef } from 'react';
import './DataEntry.css';
import { format } from 'date-fns';
import { es } from 'date-fns/locale'; 
import { useNotification } from '../context/NotificationContext';

// === HELPER API CONFIG (PRODUCTION READY) ===
const getApiUrl = () => {
    // Check for Vite env var
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) {
        return import.meta.env.VITE_API_URL;
    }
    // Check for React Create App env var
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    
    // Default to relative path for production (same domain)
    return ''; 
};

const API_URL = getApiUrl();

// --- HELPER FUNCTIONS ---
const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const getDisplayDates = () => {
    const dates = [];
    let currentDate = new Date();
    currentDate.setDate(currentDate.getDate() - 1);
    for (let i = 0; i < 5; i++) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() - 1);
    }
    return dates;
};

function DataEntry() {
    const { showNotification } = useNotification();
    const [groupedHotels, setGroupedHotels] = useState({});
    const [selectedHotelId, setSelectedHotelId] = useState('');
    const [dailyData, setDailyData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // New state for hotel historical averages
    const [hotelStats, setHotelStats] = useState({}); 
    
    const prevSelectedHotelRef = useRef();
    const displayDates = getDisplayDates();

    // Helper to get current hotel object
    const getCurrentHotel = useCallback(() => {
        if (!selectedHotelId) return null;
        for (const city in groupedHotels) {
            const found = groupedHotels[city].find(h => String(h.hotel_id) === String(selectedHotelId));
            if (found) return found;
        }
        return null;
    }, [groupedHotels, selectedHotelId]);

    const currentHotel = getCurrentHotel();

    // 1. Fetch Hotels
    useEffect(() => {
        const fetchHotels = async () => {
            setLoading(true);
            setError(null);
            try {
                const apiUrl = `${API_URL}/api.php?path=hotels&limit=999`;
                const response = await fetch(apiUrl, { headers: getAuthHeader() });

                if (!response.ok) throw new Error(`HTTP error fetching hotels! status: ${response.status}`);

                const data = await response.json();
                
                if (data.success && data.data?.hotels) {
                    const hotels = data.data.hotels;
                    const grouped = hotels.reduce((acc, hotel) => {
                        const city = hotel.city || 'Sin Ciudad';
                        if (!acc[city]) { acc[city] = []; }
                        acc[city].push(hotel);
                        return acc;
                    }, {});
                    setGroupedHotels(grouped);
                    
                    if (hotels.length > 0) {
                        setSelectedHotelId(hotels[0].hotel_id);
                    }
                } else {
                    setError('No se pudieron cargar los hoteles.');
                }
            } catch (error) {
                setError('Error de conexión al cargar hoteles.');
            }
        };
        fetchHotels();
    }, []);

    // 2. Fetch Recent Averages (Last 14 Days)
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await fetch(`${API_URL}/api.php?path=hotels/averages`, { headers: getAuthHeader() });
                if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                        setHotelStats(data.data);
                    }
                }
            } catch (err) {
                console.error("Failed to load hotel averages", err);
            }
        };
        fetchStats();
    }, []);

    // 3. Fetch Daily Metrics
    useEffect(() => {
        const fetchDailyMetrics = async (hotelId) => {
            if (!hotelId || isNaN(parseInt(hotelId))) {
                setLoading(false);
                return;
            }
            setLoading(true); 
            
            const oldestDisplayDate = displayDates[displayDates.length - 1];
            const startDate = format(oldestDisplayDate, 'yyyy-MM-dd');
            const endDate = format(new Date(), 'yyyy-MM-dd');

            try {
                const apiUrl = `${API_URL}/api.php?path=daily-metrics&hotelId=${hotelId}&startDate=${startDate}&endDate=${endDate}`;
                const response = await fetch(apiUrl, { headers: getAuthHeader() });

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                const data = await response.json();
                
                if (data.success && Array.isArray(data.data)) {
                    const loadedHotelMetrics = {}; 
                    data.data.forEach(item => {
                        loadedHotelMetrics[item.date] = {
                            occupied_rooms: item.occupied_rooms.toString(),
                            average_rate: item.average_rate.toString(),
                            saved: false
                        };
                    });
                    setDailyData(prev => ({ ...prev, [hotelId]: loadedHotelMetrics }));
                } else {
                    setDailyData(prev => ({ ...prev, [hotelId]: {} }));
                }
            } catch (err) {
                showNotification(`Failed to load metrics: ${err.message}`, 'error');
            } finally {
                setLoading(false);
            }
        };

        if (selectedHotelId && selectedHotelId !== prevSelectedHotelRef.current) {
            fetchDailyMetrics(selectedHotelId);
        } else if (!selectedHotelId && Object.keys(groupedHotels).length > 0) {
            setLoading(false);
        }
        prevSelectedHotelRef.current = selectedHotelId;
    }, [selectedHotelId, groupedHotels, showNotification, displayDates]);
    
    const handleInputChange = (dateKey, field, value) => {
        setDailyData(prevData => {
            const hotelData = prevData[selectedHotelId] || {};
            const dateEntryData = hotelData[dateKey] || { occupied_rooms: '', average_rate: '' };
            return {
                ...prevData,
                [selectedHotelId]: {
                    ...hotelData,
                    [dateKey]: { ...dateEntryData, [field]: value, saved: false }
                }
            };
        });
    };

    const handleSave = async (dateKey) => {
        const dataToSave = dailyData[selectedHotelId]?.[dateKey];
        if (!dataToSave || dataToSave.occupied_rooms === '' || dataToSave.average_rate === '') {
            showNotification('Por favor, rellene todos los campos para la fecha seleccionada.', 'warning');
            return;
        }

        // --- Capacity Validation (Blocking) ---
        const enteredRooms = parseInt(dataToSave.occupied_rooms, 10);
        const maxRooms = currentHotel?.total_rooms_available ? parseInt(currentHotel.total_rooms_available, 10) : 0;

        if (maxRooms > 0 && enteredRooms > maxRooms) {
            showNotification(`Error: El hotel solo tiene ${maxRooms} habitaciones. No puedes ingresar ${enteredRooms}.`, 'error');
            return;
        }

        // --- Rate Validation (Non-Blocking Warning) ---
        const enteredRate = parseFloat(dataToSave.average_rate);
        const stats = hotelStats[selectedHotelId];
        
        if (stats && stats.avg > 0) {
            // Logic: Catch "Fat Finger" errors (+/- 90%)
            // Low Limit: 10% of Recent Average
            // High Limit: 190% of Recent Average
            const highLimit = stats.avg * 1.9; 
            const lowLimit = stats.avg * 0.1; 
            
            if (enteredRate > highLimit || (enteredRate < lowLimit && enteredRate > 0)) {
                showNotification('Advertencia: La tarifa ingresada es inusual (muy alta o muy baja). Se guardará de todas formas.', 'warning');
            }
        }

        const payload = {
            hotel_id: parseInt(selectedHotelId),
            date: dateKey, 
            occupied_rooms: enteredRooms,
            average_rate: enteredRate
        };

        try {
            const apiUrl = `${API_URL}/api.php?path=daily-metrics-save`;
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const result = await response.json();

            if (response.ok && result.success) {
                showNotification(result.data.message || 'Datos guardados exitosamente!', 'success');
                
                setDailyData(prevData => {
                    const hotelData = prevData[selectedHotelId] || {};
                    const dateEntryData = hotelData[dateKey];
                    return {
                        ...prevData,
                        [selectedHotelId]: {
                            ...hotelData,
                            [dateKey]: { ...dateEntryData, saved: true }
                        }
                    };
                });

            } else {
                throw new Error(result.error?.message || 'Error desconocido de la API');
            }
        } catch (error) {
            showNotification(`Error al guardar: ${error.message}`, 'error');
        }
    };

    if (loading && Object.keys(groupedHotels).length === 0) return <section className="info-entry-section">Cargando Hoteles...</section>;
    if (error) return <section className="info-entry-section" style={{color: 'red'}}>{error}</section>;

    return (
        <>
            <header className="main-header">
                <h1 className="page-title">Ingreso de información - {format(new Date(), 'PP', { locale: es })}</h1>
            </header>
            <section className="info-entry-section">
                <div className="hotel-selection">
                    <label htmlFor="hotel-select">Selecciona Hotel</label>
                    <select
                        id="hotel-select"
                        value={selectedHotelId}
                        onChange={(e) => setSelectedHotelId(e.target.value)}
                        disabled={Object.keys(groupedHotels).length === 0}
                    >
                        {Object.keys(groupedHotels).sort().map(city => (
                            <optgroup key={city} label={city}>
                                {groupedHotels[city].map(hotel => (
                                    <option key={hotel.hotel_id} value={hotel.hotel_id}>
                                        {hotel.name}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                    {currentHotel && currentHotel.total_rooms_available > 0 && (
                        <span style={{ fontSize: '12px', color: '#666', marginLeft: '10px' }}>
                            (Max: {currentHotel.total_rooms_available} habs)
                        </span>
                    )}
                </div>

                {loading && <p>Cargando métricas...</p>}

                {!loading && selectedHotelId && (
                    <div className="data-table-container">
                        <table className="data-input-table">
                            <thead>
                                <tr>
                                    <th>Fecha de captura</th>
                                    <th>Cuartos ocupados</th>
                                    <th>Tarifa promedio</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayDates.map(dateObj => {
                                    const dateKey = format(dateObj, 'yyyy-MM-dd');
                                    const data = (dailyData[selectedHotelId] && dailyData[selectedHotelId][dateKey]) 
                                                ? dailyData[selectedHotelId][dateKey] 
                                                : { occupied_rooms: '', average_rate: '' };
                                    
                                    // --- VISUAL VALIDATIONS ---
                                    // 1. Capacity Check (Blocking)
                                    const maxRooms = currentHotel?.total_rooms_available ? parseInt(currentHotel.total_rooms_available, 10) : 0;
                                    const isCapacityExceeded = maxRooms > 0 && data.occupied_rooms && parseInt(data.occupied_rooms) > maxRooms;

                                    // 2. Statistical Rate Check (90% Threshold)
                                    const enteredRate = parseFloat(data.average_rate);
                                    const stats = hotelStats[selectedHotelId];
                                    let isRateAnomaly = false;
                                    let rateWarningText = '';

                                    if (stats && stats.avg > 0 && !isNaN(enteredRate) && enteredRate > 0) {
                                        const lowLimit = stats.avg * 0.1;  // 90% lower
                                        const highLimit = stats.avg * 1.9; // 90% higher
                                        
                                        if (enteredRate > highLimit || enteredRate < lowLimit) {
                                            isRateAnomaly = true;
                                            rateWarningText = `Inusual (Promedio reciente: $${stats.avg.toFixed(0)})`;
                                        }
                                    }

                                    return (
                                        <tr key={dateKey}>
                                           <td>{format(dateObj, 'PP', { locale: es })}</td>
                                            <td>
                                                <input 
                                                    type="number" 
                                                    value={data.occupied_rooms} 
                                                    onChange={(e) => handleInputChange(dateKey, 'occupied_rooms', e.target.value)} 
                                                    className="table-input"
                                                    style={{ 
                                                        borderColor: isCapacityExceeded ? 'red' : '', 
                                                        color: isCapacityExceeded ? 'red' : '',
                                                        borderWidth: isCapacityExceeded ? '2px' : '1px'
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <input 
                                                    type="number" 
                                                    step="0.01" 
                                                    value={data.average_rate} 
                                                    onChange={(e) => handleInputChange(dateKey, 'average_rate', e.target.value)} 
                                                    className="table-input"
                                                    style={{ 
                                                        borderColor: isRateAnomaly ? '#ff9800' : '', // Orange for warning
                                                        color: isRateAnomaly ? '#e65100' : '',
                                                        borderWidth: isRateAnomaly ? '2px' : '1px'
                                                    }}
                                                />
                                                {/* VISIBLE WARNING TEXT */}
                                                {isRateAnomaly && (
                                                    <div style={{ color: '#e65100', fontSize: '0.7rem', marginTop: '2px', lineHeight: '1.1', fontWeight: 'bold' }}>
                                                        {rateWarningText}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <button 
                                                    className="save-button" 
                                                    style={{ 
                                                        backgroundColor: data.saved ? '#4CAF50' : '',
                                                        borderColor: data.saved ? '#4CAF50' : '',
                                                        color: data.saved ? 'white' : ''
                                                    }}
                                                    onClick={() => handleSave(dateKey)}
                                                >
                                                    {data.saved ? 'Guardado' : 'Guardar'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </>
    );
}

export default DataEntry;